import { IntroScreen as TLIntroScreen } from '@treedis/tour-loader';
import { useCallback, useEffect, useState } from 'react';
import { getVersion } from '@/components/util';
import { useLanguage } from '@/hooks/index';
import color from '@/utils/muiColor';
import { useSmoothLoading } from '@/hooks/useSmoothLoading';

type Props = {
  rtl?: boolean;
  tour: any;
  initialDuplicates: any;
  introScreen: any;
  preventSubmit?: boolean;
  whiteLabelData?: {
    isWLUser: boolean;
    primaryColor: string;
    secondaryColor: string;
    logo: string;
    website: string;
  };
  translation: any;
  inputs: {
    name: boolean;
    email: boolean;
    phone: boolean;
  };
  checkbox: {
    legal1: string;
    legal2: string;
  };
  onStart: (data: any) => void;
  onFinish: () => void;
  isGameTour?: () => boolean;
  onUnmount?: () => void;
  preventSubmitCallback: () => void;
};

export function IntroScreen({
  rtl,
  introScreen,
  tour,
  checkbox,
  whiteLabelData,
  translation,
  initialDuplicates,
  inputs,
  preventSubmit,
  onStart,
  onFinish,
  isGameTour,
  onUnmount,
  preventSubmitCallback,
}: Props) {
  const { languageOptions, defaultLanguage, handleChange } = useLanguage({
    initialDuplicates,
    tourSlug: tour?.slug,
  });
  const [isStarted, setStarted] = useState(false);

  const { isLoading, progress } = useSmoothLoading(isStarted);

  const handleSubmit = useCallback(
    async (data: Record<string, string>) => {
      if (tour.password && !data.password) return;

      if (
        (!introScreen && data.password) ||
        (introScreen && !data.password) ||
        (isGameTour() && !introScreen && !tour.password)
      ) {
        setStarted(true);
      }

      onStart(data);
    },
    [introScreen, isGameTour, onStart, tour.password],
  );

  useEffect(() => {
    if (!isLoading && isStarted && !tour.password) {
      onFinish();
    }
  }, [isLoading, onFinish, isStarted, tour.password]);

  useEffect(() => {
    return () => {
      onUnmount?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // create a state for internal value

  const getBackgroundVideo = () => {
    if (introScreen?.videoLink) return introScreen?.videoLink;
    if (introScreen?.uploadedVideoUrl)
      return `${process.env.NEXT_PUBLIC_CDN}/${introScreen?.uploadedVideoUrl}${getVersion(
        introScreen?.updatedAt,
      )}`;
    return '';
  };

  return (
    <TLIntroScreen
      rtl={rtl}
      hideHelpButton={introScreen?.hideHelpButton}
      title={tour.showTitle ? introScreen?.title || tour.title || '' : ''}
      description={introScreen?.description || tour.address || ''}
      backgroundVideo={getBackgroundVideo()}
      loading={isStarted ? isLoading : undefined}
      loadingProgress={progress}
      termsUrl={
        tour.termsUrl ||
        (translation.termFile
          ? `${process.env.NEXT_PUBLIC_CDN}/tours/${tour.id}/termFile.pdf`
          : undefined)
      }
      isTextShadowed={introScreen?.isTextShadowed}
      enableTips={introScreen?.enableTips}
      darkBackground={introScreen?.darkBackground}
      blurBackground={introScreen?.blurBackground}
      // Client logo for public is shown in the BaseTour component
      client={{
        show: !!tour?.client && introScreen?.enableLogo,
        logo: tour?.clientId
          ? `${process.env.NEXT_PUBLIC_CDN}/clients/${tour.clientId}/logo.png${getVersion(
              tour?.client?.updatedAt || new Date(),
            )}`
          : '',
        horizontalLogo: !!tour?.client?.widthLogo,
        // eslint-disable-next-line no-nested-ternary
        website: tour?.client?.website
          ? tour?.client.website?.includes('https://')
            ? tour?.client.website
            : `https://${tour?.client.website}`
          : '',
      }}
      backgroundImage={
        introScreen?.backgroundImage
          ? `${process.env.NEXT_PUBLIC_CDN}/tours/${tour.id}/images/${
              introScreen.backgroundImage
            }${getVersion(tour.updatedAt)}`
          : `${process.env.NEXT_PUBLIC_CDN}/tours/${tour.id}/images/${tour.mainImage}${getVersion(
              tour.updatedAt,
            )}`
      }
      language={{
        options: languageOptions || [],
        default: defaultLanguage,
        onChange: handleChange,
      }}
      button={{
        label: tour?.translation?.introScreenButtonLabel,
        showPlayButton: introScreen?.showPlayButton,
        primaryColor: introScreen?.buttonPrimaryColor || '#2055ff',
        secondaryColor: introScreen?.buttonSecondaryColor || '#ffffff',
      }}
      whiteLabel={{
        show: Boolean(whiteLabelData?.logo?.length),
        isWLUser: whiteLabelData?.isWLUser,
        logo: whiteLabelData?.logo,
        website: whiteLabelData?.website,
        ...(whiteLabelData?.primaryColor
          ? {
              primaryColor: {
                light: color.light(whiteLabelData?.primaryColor),
                main: whiteLabelData?.primaryColor,
                dark: color.dark(whiteLabelData?.primaryColor),
              },
            }
          : {}),
        ...(whiteLabelData?.secondaryColor
          ? {
              secondaryColor: {
                light: color.light(whiteLabelData?.secondaryColor),
                main: whiteLabelData?.secondaryColor,
                dark: color.dark(whiteLabelData?.secondaryColor),
              },
              textColor: {
                secondary: color.lighter(whiteLabelData?.secondaryColor),
                primary: whiteLabelData?.secondaryColor,
                disabled: color.light(whiteLabelData?.secondaryColor),
              },
            }
          : {}),
      }}
      inputs={inputs}
      onSubmit={handleSubmit}
      preventSubmit={preventSubmit}
      preventSubmitCallback={preventSubmitCallback}
      translation={translation}
      checkbox={checkbox}
      showPassword={!!tour.password}
      isEnabled={!!introScreen}
    />
  );
}
